import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { doc, getDoc, onSnapshot, updateDoc, serverTimestamp } from "firebase/firestore";
import { callFunction } from "../../api/callFunction";
import { CalendarIcon, SparklesIcon } from "@heroicons/react/24/outline";
import { db } from "../../firebase";
import { buildEventDuplicateState } from "../utils/duplicateEvent";
import { buildSuggestionConversionState } from "../utils";
import { showToast } from "../../utils/showToast";
import { capture, EVENTS } from "../../analytics";

// "Your next events": the ideas drafted for this host, each one a tap from the
// create form. The same plan the nudge text points at.
//
// The plan lives on hostCadence/{vendorID}, which is server-written and
// owner-readable, so this listens to the host's own doc. Nothing renders until
// there is a plan, which is what keeps the card out of sight while the feature
// is still off.

const KIND_LABELS = {
  repeat: "You have run this before",
  suggestion: "From the idea feed",
  new: "New idea",
};

/** The date an idea was planned for, or null if it is not a usable future date. */
export const parseIdeaDate = (idea) => {
  const raw = Number.isFinite(idea?.dateMs) ? new Date(idea.dateMs) : new Date(idea?.date || "");
  if (!Number.isFinite(raw.getTime())) return null;
  return raw.getTime() > Date.now() ? raw : null;
};

/**
 * An idea as the create form's AI chat reads it. Same shape
 * buildSuggestionConversionState produces, so both handoffs look alike.
 */
export const buildIdeaMessage = (idea) => {
  const when = parseIdeaDate(idea);
  const parts = [];
  if (idea?.title) parts.push(`Event: ${idea.title}`);
  if (idea?.venue) parts.push(`Venue: ${idea.venue}`);
  if (when) {
    parts.push(`Date: ${when.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" })}`);
    parts.push(`Time: ${when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`);
  }
  if (idea?.blurb) parts.push(`\n${idea.blurb}`);
  if (idea?.why) parts.push(`\nWhy: ${idea.why}`);
  return parts.join("\n");
};

const dateLabel = (idea) => {
  const when = parseIdeaDate(idea);
  if (!when) return "";
  return when.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

const IdeaCard = ({ idea, busy, onCreate }) => (
  <div className="flex flex-col p-3 bg-white dark:bg-gray-800 rounded-lg border border-dashed border-primary/40 shadow-sm">
    <div className="flex items-start gap-2">
      <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
        <CalendarIcon className="h-5 w-5 text-primary/70" />
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug">{idea.title}</h3>
        <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
          {dateLabel(idea)}{idea.venue ? ` · ${idea.venue}` : ""}
        </div>
      </div>
    </div>
    {idea.blurb ? <p className="mt-2 text-xs text-gray-600 dark:text-gray-300">{idea.blurb}</p> : null}
    {idea.why ? <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 italic">{idea.why}</p> : null}
    <div className="mt-3 flex items-center justify-between gap-2">
      <span className="text-xs text-gray-400 dark:text-gray-500">{KIND_LABELS[idea.kind] || ""}</span>
      <button
        type="button"
        onClick={onCreate}
        disabled={busy}
        className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-primary/90 disabled:opacity-60"
      >
        Create this event
      </button>
    </div>
  </div>
);

export const NextEventsPlanSection = ({ vendorID }) => {
  const navigate = useNavigate();
  const [state, setState] = useState(null);
  const [busyId, setBusyId] = useState("");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    if (!vendorID) return undefined;
    const unsubscribe = onSnapshot(
      doc(db, "hostCadence", vendorID),
      (snap) => setState(snap.exists() ? snap.data() : null),
      // A host with no plan yet, or rules that have not caught up with a deploy:
      // either way the card simply stays away.
      (err) => console.warn("Could not read your event plan:", err.message)
    );
    return () => unsubscribe();
  }, [vendorID]);

  const plan = state?.plan;
  const ideas = Array.isArray(plan?.ideas) ? plan.ideas : [];

  const handleCreate = async (idea) => {
    setBusyId(idea.id);
    capture(EVENTS.HOST_PLAN_IDEA_CLICKED, { kind: idea.kind, source: plan?.source || null });
    const when = parseIdeaDate(idea);
    try {
      if (idea.kind === "repeat" && idea.sourceEventId) {
        const snap = await getDoc(doc(db, "events", idea.sourceEventId));
        if (snap.exists()) {
          navigate("/events/new", { state: buildEventDuplicateState({ id: snap.id, ...snap.data() }, { date: when }) });
          return;
        }
      }
      if (idea.kind === "suggestion" && idea.sourceSuggestionId) {
        const snap = await getDoc(doc(db, "eventsSuggestions", idea.sourceSuggestionId));
        const data = snap.exists() ? snap.data() : null;
        const mine = !data?.pickedUpBy || data.pickedUpBy === vendorID;
        if (data && data.status === "pending" && mine) {
          // Claim it so an abandoned form lands in "Finish creating" rather than
          // disappearing. Best effort: someone else may have claimed it first.
          try {
            await updateDoc(snap.ref, { pickedUpBy: vendorID, pickedUpAt: serverTimestamp() });
          } catch (err) {
            console.warn("Could not claim that idea:", err.message);
          }
          navigate("/events/new", { state: buildSuggestionConversionState({ id: snap.id, ...data }) });
          return;
        }
      }
      // A new idea, and the fallback whenever the source is gone or taken.
      navigate("/events/new", { state: { suggestionMessage: buildIdeaMessage(idea) } });
    } catch (err) {
      console.error("Could not open that idea:", err);
      showToast("Could not open that idea. Try again.", { type: "error" });
    } finally {
      setBusyId("");
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Longer than the function's own timeout, so a slow plan finishes rather
      // than leaving the host with an error and a plan that saved anyway.
      const data = await callFunction("refreshMyHostPlan", {}, { timeout: 190000 });
      const count = data?.plan?.ideas?.length || 0;
      capture(EVENTS.HOST_PLAN_REFRESHED, { ideaCount: count, source: data?.plan?.source || null });
      showToast(count ? "Fresh ideas are ready." : "We could not draft anything new right now.");
    } catch (err) {
      console.error("refreshMyHostPlan failed", err);
      showToast(err?.message || "Could not draft new ideas.", { type: "error" });
    } finally {
      setRefreshing(false);
    }
  };

  const setCadence = async (payload, message, analytics) => {
    try {
      await callFunction("setMyHostCadence", payload, { timeout: 60000 });
      if (analytics) capture(analytics.name, analytics.props);
      showToast(message);
    } catch (err) {
      console.error("setMyHostCadence failed", err);
      showToast(err?.message || "Could not change that.", { type: "error" });
    }
  };

  if (!vendorID || ideas.length === 0) return null;

  const optedOut = state?.hostOptOut === true;
  return (
    <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 dark:bg-primary/10 p-4">
      <div className="flex items-center gap-2">
        <SparklesIcon className="h-5 w-5 text-primary" />
        <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Your next events</h2>
        <span className="inline-flex items-center justify-center min-w-[1.25rem] px-1.5 py-0.5 rounded-full text-xs font-medium bg-primary text-white">
          {ideas.length}
        </span>
      </div>
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
        Ideas for the next few months, drawn from what you have run before. Nothing is created until you save it.
      </p>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
        {ideas.map((idea) => (
          <IdeaCard key={idea.id} idea={idea} busy={busyId === idea.id} onCreate={() => handleCreate(idea)} />
        ))}
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs">
        <button type="button" onClick={handleRefresh} disabled={refreshing} className="text-primary hover:underline disabled:opacity-60">
          {refreshing ? "Drafting…" : "Refresh ideas"}
        </button>
        <button
          type="button"
          onClick={() => setCadence({ snoozeDays: 30 }, "We will check back in a month.")}
          className="text-gray-500 dark:text-gray-400 hover:underline"
        >
          Remind me in a month
        </button>
        <button
          type="button"
          onClick={() => setCadence(
            { optOut: !optedOut },
            optedOut ? "These texts are back on." : "We will stop texting you about this.",
            { name: EVENTS.HOST_NUDGE_OPT_OUT, props: { optOut: !optedOut } }
          )}
          className="text-gray-500 dark:text-gray-400 hover:underline"
        >
          {optedOut ? "Turn these texts back on" : "Turn off these texts"}
        </button>
      </div>
    </div>
  );
};

export default NextEventsPlanSection;
